import * as React from "react";
import { useInstanceState } from "../instance/instance-store";
import { DEFAULT_TITLE, useTitleContext } from "../../contexts/TitleContext";
import { useRouteType } from "../../hooks/useRouteType";
import { QuickAgentChatPanelV2 } from "./QuickAgentChatPanelV2";
import { getSearchChatSignature, SearchChatContext, useSearchChatContext } from "./search-chat-context-store";

/**
 * Wrapper around QuickAgentChatPanel that manages visibility and constructs
 * a one-time initialPrompt with page and instance context for the chat agent.
 *
 * Visibility rules:
 * - Home pages: never create the panel. A panel created elsewhere stays mounted and visible.
 * - Search pages: the search creates the panel, open at 70% width, seeded with the search
 *   (term, instance, filters) as silent context. A changed search (new term or filters, not sort
 *   or page) remounts the iframe: new search, new conversation. Leaving search keeps the
 *   conversation and returns the panel to its default width.
 * - Admin pages: panel completely hidden
 * - Other pages: collapsed sidebar tab with the cold-start prompt below
 *
 * Context is a COLD-START-ONLY input. On the first render where instance + a
 * usable title (or a non-page route) are available, we build the initialPrompt
 * and FREEZE it for the lifetime of this component instance. Later renders —
 * e.g. the page title changing (or resetting to the default) during in-app
 * navigation, or an in-tab instance switch — must NOT rebuild it: the iframe
 * src is derived from this value, so changing it would remount the iframe and
 * discard the live conversation. Fresh context only happens when a brand-new
 * frame is created (full reload / new tab), which remounts this component and
 * re-runs the capture from scratch.
 */
const PROMPT_PREFIX = "This is silent context to be passed to the chat agent, the user is currently";
const PROMPT_SUFFIX = "Introduce yourself to the user by using their first name and tell them your name and what your capabilities are and ask if they need any help navigating PathFinder with you.";

/** Search prompt (silent): where the user is, what they searched for, and how to help. Instance only when known. */
const buildSearchPrompt = (search: SearchChatContext): string => {
    const where = search.instanceId
        ? `the PathFinder search results page of the ${search.instanceId} instance`
        : "the PathFinder global search results page, which searches across all team sites";
    const filters = search.filters.length ? `, filtered by ${search.filters.join("; ")}` : "";
    return `This is silent context to be passed to the chat agent. The user is on ${where}, looking at the results for the search "${search.queryText}"${filters}. `
        + "The search results are visible to the user next to this chat. Help them answer this query agentically: use your knowledge and PathFinder content to give a direct answer, "
        + "cite the relevant PathFinder pages, and offer to go deeper or refine the search. Do not introduce yourself and do not repeat the query back to the user.";
};

/** What is mounted: a React key (a changed key remounts the iframe) and the prompt it was mounted with. */
interface MountedChat {
    key: string;
    prompt: string;
    fromSearch: boolean;
}

export const QuickAgentChatPanelWrapperV2: React.FC<{ agentUrl: string }> = ({ agentUrl }) => {
    const { isAdmin, isHome, isAnySearch, searchInstanceId } = useRouteType();
    const { instanceId } = useInstanceState();
    const { title } = useTitleContext();
    const searchContext = useSearchChatContext(state => state.context);

    // Home never creates the panel: wait until the user has been on a non-home route once.
    // Never goes back to false, so a panel created elsewhere stays through later home visits.
    const [hasLeftHome, setHasLeftHome] = React.useState<boolean>(() => !isHome);
    React.useEffect(() => {
        if (!isHome) setHasLeftHome(true);
    }, [isHome]);

    // The candidate prompt for THIS render. Returns undefined until the cold-start
    // context is ready (instance present, and on a page route the real page title
    // has been set). This may change across renders as the title changes — the
    // latch below captures only the first ready value and ignores the rest.
    const candidateQuery = React.useMemo(() => {
        if (!instanceId) return undefined;

        const hasTitle = title && title !== DEFAULT_TITLE;
        if (hasTitle) {
            return `${PROMPT_PREFIX} on the page "${title}" on the ${instanceId} instance. ${PROMPT_SUFFIX}`;
        }

        // On non-page routes the title is never customized, use instance-only context
        const isPageRoute = /^#\/page\//.test(window.location.hash);
        if (!isPageRoute) {
            return `${PROMPT_PREFIX} on the ${instanceId} instance. ${PROMPT_SUFFIX}`;
        }

        // On page routes, wait for title to be set by the page component
        return undefined;
    }, [instanceId, title]);

    // What is mounted. Set once from the cold-start candidate (and never changed by later title or
    // instance changes), or replaced whenever a search with a new signature is shown. It persists
    // when the user leaves search, so the conversation follows them to pages and home.
    const [mounted, setMounted] = React.useState<MountedChat | undefined>(undefined);
    // Only a context published by the page of the current route counts: between an instance search and
    // a global search the store can briefly still hold the previous page's search, which must not seed
    // (and then immediately discard) a chat under the new route.
    const contextMatchesRoute = !!searchContext && (searchContext.instanceId ?? undefined) === searchInstanceId;
    const searchSignature = isAnySearch && searchContext && contextMatchesRoute ? getSearchChatSignature(searchContext) : undefined;

    React.useEffect(() => {
        if (searchSignature && searchContext && mounted?.key !== searchSignature) {
            setMounted({ key: searchSignature, prompt: buildSearchPrompt(searchContext), fromSearch: true });
            return;
        }
        if (!mounted && hasLeftHome && candidateQuery !== undefined && !isAnySearch) {
            setMounted({ key: "cold-start", prompt: candidateQuery, fromSearch: false });
        }
    }, [searchSignature, searchContext, mounted, hasLeftHome, candidateQuery, isAnySearch]);

    // Hide completely on admin pages. (Admin is configuration-only and is opened
    // in a new tab, so this does not unmount an active chat on a content page.)
    if (isAdmin) {
        return null;
    }

    // Nothing to show until a search or a ready cold-start context has created the panel.
    if (!mounted) {
        return null;
    }

    return (
        <QuickAgentChatPanelV2
            key={mounted.key}
            agentUrl={agentUrl}
            initialQuery={mounted.prompt}
            defaultCollapsed={!mounted.fromSearch}
            searchLayout={isAnySearch && mounted.fromSearch}
        />
    );
};
